import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { Card, Button, Space, Spin, message, Breadcrumb, Tag, Tooltip, Empty } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  HomeOutlined,
  AuditOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Show } from "@refinedev/antd";
import { usePageTitle } from "../../hooks";
import { auditPlotService } from "../../services/api";
import { PlotMapEditor } from "./components/PlotMapEditor";
import { MAP_COLORS } from "./components/PlotMapEditor";
import "leaflet/dist/leaflet.css";

interface HistoricalPlot {
  id: number;
  name: string;
  surface: number;
  surface_gps: number;
  season_name: string | null;
  season_year: number | null;
  plot_type_name: string | null;
  rootstock_name: string | null;
  plants: number;
  is_active: boolean;
  crop_date: number;
  kg_plot: number;
}

/**
 * PlotGeometryMap Component
 * Full-page map for managing audit plot geometry.
 *
 * - If the plot already has geometry it is shown on the editable map.
 *   The user can edit/delete it with the leaflet-draw toolbar.
 * - If the plot has NO geometry the sidebar offers two paths:
 *     1. Validate a historical geometry (click on a card to load it).
 *     2. Draw a new polygon with the leaflet-draw toolbar on the map.
 */
export const PlotGeometryMap = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: auditId, plotId } = useParams<{ id: string; plotId: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [hasExistingGeometry, setHasExistingGeometry] = useState(false);
  const [geometrySource, setGeometrySource] = useState<string>("none");
  const [referencePlots, setReferencePlots] = useState<GeoJSON.FeatureCollection | null>(null);
  const [historicalPlots, setHistoricalPlots] = useState<GeoJSON.FeatureCollection | null>(null);
  const [routeData, setRouteData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [historicalFeatures, setHistoricalFeatures] = useState<GeoJSON.Feature[]>([]);
  const [selectedHistorical, setSelectedHistorical] = useState<GeoJSON.Feature | null>(null);
  const [plotName, setPlotName] = useState("");
  const [auditReference, setAuditReference] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  usePageTitle(plotName ? `Geometría - ${plotName}` : "Geometría de Parcela");

  // Load all data on mount
  useEffect(() => {
    if (!plotId) return;
    loadData();
  }, [plotId]);

  const loadData = async () => {
    if (!plotId || !auditId) return;
    setLoading(true);

    try {
      const [plotRes, geoRes, auditRes] = await Promise.all([
        auditPlotService.getPlot(plotId),
        auditPlotService.getPlotGeometry(plotId),
        import("../../services/api/apiClient").then(({ apiClient }) =>
          apiClient.get(`/audits/${auditId}/`)
        ),
      ]);

      const plotData = (plotRes as any).properties || plotRes;
      setPlotName(plotData.name || "");
      setAuditReference(auditRes.data.reference || auditId);

      if (geoRes.geom) {
        const parsedGeom =
          typeof geoRes.geom === "string" ? JSON.parse(geoRes.geom) : geoRes.geom;
        setGeometry(parsedGeom);
        setHasExistingGeometry(true);
        setGeometrySource(geoRes.source || "current_plot");
      } else {
        setGeometrySource("none");
      }

      try {
        const refPlots = await auditPlotService.getReferencePlots(plotId);
        setReferencePlots(refPlots as any);
      } catch (err) {
        console.error("[PlotGeometryMap] reference-plots error:", err);
        setReferencePlots(null);
      }

      try {
        const historical = await auditPlotService.getHistoricalGeometry(plotId);
        setHistoricalPlots(historical as any);
        setHistoricalFeatures(historical.features || []);
      } catch {
        setHistoricalPlots(null);
        setHistoricalFeatures([]);
      }

      try {
        const route = await auditPlotService.getRouteGeometry(plotId);
        setRouteData(route as any);
      } catch {
        setRouteData(null);
      }
    } catch (error: any) {
      message.error(error.message || "Error al cargar los datos de la parcela");
    } finally {
      setLoading(false);
    }
  };

  const handleGeometryChange = useCallback(
    (newGeometry: GeoJSON.Geometry | null) => {
      setGeometry(newGeometry);
      setHasChanges(true);
      setGeometrySource("current_plot");
    },
    []
  );

  const handleValidateHistorical = (feature: GeoJSON.Feature) => {
    setSelectedHistorical(feature);
    setGeometry(feature.geometry);
    setHasChanges(true);
    setGeometrySource("current_plot");
    const props = feature.properties as HistoricalPlot;
    message.success(
      `Geometría de "${props.name}" (${props.season_name || "Sin campaña"}) cargada. Guarda para confirmar.`
    );
  };

  const handleSave = async () => {
    if (!plotId || !auditId) return;
    setSaving(true);

    try {
      await auditPlotService.updatePlot(plotId, { geom: geometry });
      message.success("Geometría guardada correctamente");
      navigate(`/audits/${auditId}`);
    } catch (error: any) {
      message.error(error.message || "Error al guardar la geometría");
    } finally {
      setSaving(false);
    }
  };

  // Filter historical plots matching the audit plot name
  const matchingHistorical = historicalFeatures.filter(
    (f) =>
      (f.properties as HistoricalPlot).name?.toLowerCase() === plotName.toLowerCase()
  );
  const otherHistorical = historicalFeatures.filter(
    (f) =>
      (f.properties as HistoricalPlot).name?.toLowerCase() !== plotName.toLowerCase()
  );

  const breadcrumb = (
    <Breadcrumb
      items={[
        {
          title: (
            <Link to="/">
              <HomeOutlined /> {t("home.title", "Inicio")}
            </Link>
          ),
        },
        {
          title: (
            <Link to="/audits">
              <AuditOutlined /> {t("audit.title", "Auditorías")}
            </Link>
          ),
        },
        {
          title: <Link to={`/audits/${auditId}`}>{auditReference || auditId}</Link>,
        },
        {
          title: (
            <>
              <EnvironmentOutlined /> Geometría - {plotName}
            </>
          ),
        },
      ]}
    />
  );

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Spin size="large" tip="Cargando datos del mapa..." />
      </div>
    );
  }

  return (
    <Show
      isLoading={false}
      title={`Geometría - ${plotName}`}
      breadcrumb={breadcrumb}
      headerButtons={() => (
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/audits/${auditId}`)}>
            {t("common.back", "Volver")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges}
          >
            Guardar Geometría
          </Button>
        </Space>
      )}
    >
      <div style={{ display: "flex", gap: 16, height: "calc(100vh - 220px)", minHeight: 500 }}>
        {/* Left panel */}
        <Card
          size="small"
          title={hasExistingGeometry ? "Geometría actual" : "Asignar geometría"}
          style={{ width: 340, flexShrink: 0, overflow: "auto" }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {/* Status indicator */}
            <div>
              {geometry ? (
                <Tag color="green" icon={<CheckCircleOutlined />}>
                  Geometría definida
                </Tag>
              ) : (
                <Tag color="orange">Sin geometría</Tag>
              )}
              {hasChanges && <Tag color="blue">Cambios sin guardar</Tag>}
            </div>

            {/* Instructions depending on whether geometry exists */}
            {hasExistingGeometry ? (
              <div style={{ fontSize: 13, color: "#666" }}>
                <p>La geometría actual se muestra en el mapa.</p>
                <p>Usa los botones de la barra de herramientas del mapa para editarla o eliminarla.</p>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#666" }}>
                <p>Esta parcela no tiene geometría. Puedes:</p>
                <ul style={{ paddingLeft: 18, margin: "4px 0" }}>
                  {historicalFeatures.length > 0 && (
                    <li>Seleccionar una geometría de años anteriores de la lista de abajo.</li>
                  )}
                  <li>Dibujar un polígono nuevo con el botón de la barra de herramientas del mapa.</li>
                </ul>
              </div>
            )}

            {/* Historical plots list — only when there is NO existing geometry */}
            {!hasExistingGeometry && historicalFeatures.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  Geometrías de años anteriores
                </div>

                {matchingHistorical.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                      Coinciden con "{plotName}":
                    </div>
                    {matchingHistorical.map((feature) => {
                      const props = feature.properties as HistoricalPlot;
                      const isSelected = selectedHistorical?.id === feature.id;
                      return (
                        <Card
                          key={feature.id}
                          size="small"
                          style={{
                            marginBottom: 8,
                            cursor: "pointer",
                            borderColor: isSelected ? "#1890ff" : undefined,
                            backgroundColor: isSelected ? "#e6f7ff" : undefined,
                          }}
                          onClick={() => handleValidateHistorical(feature)}
                        >
                          <div style={{ fontSize: 13 }}>
                            <div style={{ fontWeight: 600 }}>{props.name}</div>
                            <div>Campaña: {props.season_name || "N/A"}</div>
                            <div>Superficie: {props.surface?.toFixed(2)} ha</div>
                            <div>Plantas: {props.plants}</div>
                            {props.is_active && <Tag color="green" style={{ marginTop: 4 }}>Activa</Tag>}
                          </div>
                        </Card>
                      );
                    })}
                  </>
                )}

                {otherHistorical.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4, marginTop: 12 }}>
                      Otras parcelas de la finca:
                    </div>
                    {otherHistorical.map((feature) => {
                      const props = feature.properties as HistoricalPlot;
                      const isSelected = selectedHistorical?.id === feature.id;
                      return (
                        <Tooltip key={feature.id} title="Clic para usar esta geometría">
                          <Card
                            size="small"
                            style={{
                              marginBottom: 8,
                              cursor: "pointer",
                              borderColor: isSelected ? "#1890ff" : undefined,
                              backgroundColor: isSelected ? "#e6f7ff" : undefined,
                            }}
                            onClick={() => handleValidateHistorical(feature)}
                          >
                            <div style={{ fontSize: 12 }}>
                              <div style={{ fontWeight: 600 }}>{props.name}</div>
                              <div>Campaña: {props.season_name || "N/A"}</div>
                              <div>Sup: {props.surface?.toFixed(2)} ha</div>
                            </div>
                          </Card>
                        </Tooltip>
                      );
                    })}
                  </>
                )}
              </div>
            )}

            {!hasExistingGeometry && historicalFeatures.length === 0 && (
              <Empty
                description="No hay geometrías históricas disponibles"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}

            {/* Color legend */}
            <Card size="small" style={{ backgroundColor: "#fafafa" }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Leyenda</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 18,
                      height: 12,
                      backgroundColor: MAP_COLORS.reference.fill,
                      border: `2px solid ${MAP_COLORS.reference.stroke}`,
                      borderRadius: 2,
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Parcelas de referencia (finca)</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 18,
                      height: 12,
                      backgroundColor: MAP_COLORS.historical.fill,
                      border: `2px solid ${MAP_COLORS.historical.stroke}`,
                      borderRadius: 2,
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Parcelas históricas (PlotReal)</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 18,
                      height: 4,
                      backgroundColor: MAP_COLORS.route.stroke,
                      borderRadius: 2,
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Ruta del día</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 18,
                      height: 12,
                      backgroundColor: MAP_COLORS.auditPlot.fill,
                      border: `2px solid ${MAP_COLORS.auditPlot.stroke}`,
                      borderRadius: 2,
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Parcela auditada</span>
                </div>
              </div>
            </Card>
          </Space>
        </Card>

        {/* Right panel - Map */}
        <div
          style={{
            flex: 1,
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <PlotMapEditor
            geometry={geometry}
            plotName={plotName}
            onGeometryChange={handleGeometryChange}
            editable={true}
            referencePlots={referencePlots}
            historicalPlots={historicalPlots}
            routeData={routeData}
            geometrySource={geometrySource}
          />
        </div>
      </div>
    </Show>
  );
};
